import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  DeleteOrderAttachment,
  GetOrderAttachments,
  UploadOrderAttachment,
} from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";
import DeleteConfirmation from "../common/DeleteConfirmation";
import RowActionsMenu from "../common/RowActionsMenu";
import { NotificationManager } from "react-notifications";

const OrderAttachmentsTable = ({ orderNumber }) => {
  const columns = ["Title", "Category", "Actions"];
  const columnHeaders = ["title", "webdocsCategory"];
  const rowsPerPageOptions = [5, 10, 25];

  const categoryOptions = ["SOPS", "WEBDOCS", "SOP", "InternalDocs", "COCS"];

  const [attachments, setAttachments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [loading, setLoading] = useState(true);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedAttachmentToDelete, setSelectedAttachmentToDelete] = useState(null);

  // Upload dialog states
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("WEBDOCS");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAttachments();
  }, [orderNumber]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const data = await GetOrderAttachments(orderNumber);
      setAttachments(data.results || data);
    } catch (error) {
      console.error("Error fetching attachments:", error);
      NotificationManager.error("Error fetching attachments");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (attachment) => {
    // Implement edit functionality if needed
  };

  const handleAddClick = () => {
    setIsUploadDialogOpen(true);
    setUploadTitle("");
    setUploadCategory("WEBDOCS");
    setSelectedFile(null);
  };

  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    setUploadTitle("");
    setUploadCategory("WEBDOCS");
    setSelectedFile(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-populate title with filename if title is empty
      if (!uploadTitle) {
        setUploadTitle(file.name);
      }
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      NotificationManager.warning("Please select a file to upload");
      return;
    }

    if (!uploadTitle.trim()) {
      NotificationManager.warning("Please enter a title for the attachment");
      return;
    }

    if (!uploadCategory) {
      NotificationManager.warning("Please select a category");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", uploadTitle.trim());
      formData.append("category", uploadCategory);
      formData.append("orderNumber", orderNumber);

      await UploadOrderAttachment(formData);

      NotificationManager.success("Attachment uploaded successfully");
      handleUploadDialogClose();

      // Refresh the attachments list
      await fetchAttachments();
    } catch (error) {
      console.error("Error uploading attachment:", error);
      NotificationManager.error(
        error.message || "Error uploading attachment. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (attachment) => {
    setSelectedAttachmentToDelete(attachment);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteOrderAttachment(orderNumber, selectedAttachmentToDelete.recordid);
      NotificationManager.success("Attachment deleted successfully");
      setAttachments(
        attachments.filter((a) => a.id !== selectedAttachmentToDelete.id)
      );
    } catch (error) {
      console.error("Error deleting attachment:", error);
      NotificationManager.error("Error deleting attachment");
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleViewClick = (attachment) => {
    const viewerUrl = `/orders/${orderNumber}/document/${encodeURIComponent(
      attachment.physicalfile
    )}`;

    window.open(viewerUrl, "_blank");
  };


  const renderActions = (attachment) => (
    <RowActionsMenu
      ariaLabel={`actions for attachment ${attachment.title || ""}`}
      actions={[
        {
          label: "Edit",
          icon: <EditIcon />,
          color: "orange",
          onClick: () => handleEditClick(attachment),
        },
        {
          label: "View",
          icon: <VisibilityIcon />,
          color: "green",
          onClick: () => handleViewClick(attachment),
        },
        {
          label: "Delete",
          icon: <DeleteIcon />,
          color: "red",
          onClick: () => handleDeleteClick(attachment),
        },
      ]}
    />
  );

  return (
    <Paper elevation={3} sx={{ padding: 3 }} style={{ maxWidth: "650px", margin: "10px" }}>
      <Typography variant="h13" style={{ color: "rgb(25,118,210)" }}>
        Order Attachments
      </Typography>

      <Divider sx={{ mb: 1 }} />
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddClick}
        sx={{ mb: 2 }}
      >
        New
      </Button>
      <TableContainer>
        <Table sx={{ minWidth: 550 }} size="small">
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((attachment, index) => (
                <TableRow
                  key={attachment.id || `${attachment.description}-${index}`}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  {columnHeaders.map((column) => (
                    <TableCell key={column}>{attachment[column]}</TableCell>
                  ))}
                  <TableCell>{renderActions(attachment)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={attachments.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Attachment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              fullWidth
              required
              placeholder="Enter attachment title"
            />

            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={uploadCategory}
                label="Category"
                onChange={(e) => setUploadCategory(e.target.value)}
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <input
                accept="*/*"
                style={{ display: "none" }}
                id="file-upload-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Choose File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            color="primary"
            disabled={uploading || !selectedFile}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this attachment?"
        color="red"
      />
      {loading && <CommonLoader />}
    </Paper>
  );
};

export default OrderAttachmentsTable;